import { useEffect, useState } from 'react';
import { listIngredientNames } from './api';
import { navigate, withSearchParams } from './router';
import {Button, Icon, TextInput } from './ui';

/**
 * Recherche « avec ce que j'ai » (US-29).
 *
 * La recherche plein texte répond à « où est cette recette ? ». Celle-ci
 * répond à une autre question, la vraie du soir en semaine : « j'ai des
 * courgettes et de la crème, qu'est-ce que je fais ? ». Rien ne permettait de
 * combiner plusieurs ingrédients, et c'est le principal reproche fait à
 * Crouton.
 *
 * Les ingrédients sont des **jetons** et non une chaîne libre : on en retire un
 * sans retaper les autres, et l'écart avec la recherche plein texte se voit au
 * premier coup d'œil.
 */
export interface PantrySearchProps {
  /** Ingrédients actifs, tels que portés par l'URL. */
  pantry: string[];
  url: string;
}

export function PantrySearch({ pantry, url }: PantrySearchProps): React.JSX.Element {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(pantry.length > 0);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Chargées seulement quand le mode est ouvert : c'est une aide à la frappe,
  // elle n'a aucune raison de coûter une requête à qui ne s'en sert pas.
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    listIngredientNames(controller.signal)
      .then(setSuggestions)
      .catch(() => { /* une faute de frappe reste corrigible à la main */ });
    return () => { controller.abort(); };
  }, [open]);

  function apply(next: string[]): void {
    // Remplace la recherche plein texte plutôt que de s'y ajouter : ce sont
    // deux questions différentes, les mélanger ne donnerait de réponse à
    // aucune des deux.
    navigate(withSearchParams(url, { avec: next.join(','), q: '' }), { replace: true });
  }

  function add(): void {
    const name = draft.trim();
    if (name === '') return;
    setDraft('');
    if (pantry.some((existing) => existing.localeCompare(name, 'fr', { sensitivity: 'base' }) === 0)) return;
    apply([...pantry, name]);
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="compact"
        className="w-fit self-start"
        onClick={() => { setOpen(true); }}
      >
        Chercher avec ce que j’ai
      </Button>
    );
  }

  return (
    <section aria-label="Recherche par ingrédients" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-sm">
          Chercher avec ce que j’ai
          <TextInput
            list="pantry-suggestions"
            value={draft}
            placeholder="ex. courgette"
            onChange={(e) => { setDraft(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            className="w-56"
          />
        </label>
        <datalist id="pantry-suggestions">
          {suggestions.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
        <Button type="button" variant="secondary" onClick={add} disabled={draft.trim() === ''}>
          Ajouter
        </Button>
        {pantry.length > 0 && (
          <Button type="button" variant="ghost" size="compact" onClick={() => { apply([]); setOpen(false); }}>
            Quitter ce mode
          </Button>
        )}
      </div>

      {pantry.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {pantry.map((name) => (
            <li key={name}>
              <Button
                type="button"
                variant="secondary"
                size="compact"
                aria-label={`Retirer ${name}`}
                onClick={() => { apply(pantry.filter((other) => other !== name)); }}
              >
                {name} <Icon name="croix" size={0.8} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
